// The motion a contact card opens its drawer with: the card's colour floods
// from where the card sits into the drawer, which then shows through as the
// colour fades. Only clip-path and opacity animate (no layout), on an overlay
// outside React, so nothing re-renders while it runs.

/** The flood and the drawer's own reveal share one curve. */
export const DRAWER_EASING = "cubic-bezier(0.2, 0, 0, 1)";

// The flood grows for the first 70%, then fades for the rest.
const FLOOD_MS = 480;
const GROWN = 0.7;
const CARD_RADIUS = 8;

/**
 * Where a flood starts: the clicked card's box, for the person it opens;
 * `key` tells two clicks on one card apart.
 */
export interface FloodOrigin {
  rect: DOMRect;
  contactId: string;
  key: number;
}

export const floodOrigin = (el: Element, contactId: string): FloodOrigin => ({
  rect: el.getBoundingClientRect(),
  contactId,
  key: performance.now(),
});

export const prefersReducedMotion = () => window.matchMedia("(prefers-reduced-motion: reduce)").matches;

// One flood at a time: a quick second click starts over from its own card.
let running: HTMLElement | null = null;

interface Box {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

/**
 * Flood from `from` into `to` (viewport boxes) above everything in `zIndex`.
 * `paper` (the drawer) stays hidden until the colour covers it, so the flood,
 * not the drawer, is what grows; `content` rises in as the colour fades.
 */
export function flood(from: Box, to: Box, { zIndex, paper, content }: { zIndex: number; paper?: HTMLElement; content?: HTMLElement }) {
  running?.remove();
  running = null;
  if (prefersReducedMotion()) return;

  const w = window.innerWidth;
  const h = window.innerHeight;
  const inset = (b: Box, radius: number) =>
    `inset(${b.top}px ${w - b.right}px ${h - b.bottom}px ${b.left}px round ${radius}px)`;

  const overlay = document.createElement("div");
  Object.assign(overlay.style, {
    position: "fixed",
    inset: "0",
    zIndex: String(zIndex),
    pointerEvents: "none",
    background: "var(--mui-palette-people-light)",
    willChange: "clip-path, opacity",
  });
  overlay.setAttribute("aria-hidden", "true");
  document.body.appendChild(overlay);
  running = overlay;

  overlay
    .animate(
      [
        { clipPath: inset(from, CARD_RADIUS), opacity: 1, easing: DRAWER_EASING },
        { clipPath: inset(to, 0), opacity: 1, offset: GROWN, easing: "ease-out" },
        { clipPath: inset(to, 0), opacity: 0 },
      ],
      { duration: FLOOD_MS },
    )
    .finished.catch(() => undefined)
    .finally(() => {
      overlay.remove();
      if (running === overlay) running = null;
    });
  paper?.animate(
    [{ opacity: 0 }, { opacity: 0, offset: GROWN - 0.01 }, { opacity: 1, offset: GROWN }, { opacity: 1 }],
    { duration: FLOOD_MS },
  );
  content?.animate(
    [
      { opacity: 0, transform: "translateY(6px)" },
      { opacity: 0, transform: "translateY(6px)", offset: GROWN - 0.1, easing: DRAWER_EASING },
      { opacity: 1, transform: "none" },
    ],
    { duration: FLOOD_MS + 60 },
  );
}
